// UserForm component is used for adding, editing, and managing user information.

import { useContext, useEffect, useState } from "react";
import { Button, Form, Select, Row, Col, Space, Tooltip, DatePicker } from "antd";
import Input from "../../../components/input";
import dayjs from "dayjs";
import FormLayout, {
  FormContent,
  FormFooter,
  FormHeader,
} from "../../../components/form/form";
import { FormRow } from "../../../components/form/form-row";
import {
  TDropdownOptions,
  TResponseModel,
  TRole,
  TUser,
} from "../../../types/config";
import { eResultCode } from "../../../utils/enum";
import useFetch from "../../../hooks/useFetch";
import useToast from "../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import {
  AddEditUser,
  GetRoleList,
  GetSpecificUserData,
  GetAllClients,
  GetDesignations,
  GetReviewerList,
} from "../../../utils/api.constant";
import Utils from "../../../utils";
import { PageSize } from "../../../utils/constants";
import { UserContext } from "../../../context";
import EncryptUtils from "../../../utils/encrypt";
import styles from "./users.module.css";
import { requiredFieldValidator } from "../../../utils/input.validator";
import {
  CloseOutlined,
  CheckOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import utils from "../../../utils";

type Client = {
  clientId: string;
  clientName: string;
};

type TDrawerProps = {
  id: number;
  isOpen?: any;
  activeStake: string;
  onCloseDrawer: () => void;
  onRefreshList: () => void;
};

/**
 * UserForm component is used for adding, editing, and managing user information.
 * It provides a form with fields for user details such as display name, email, phone number,
 * stakeholder, role, and organization. The component handles fetching and setting dropdown options
 * for stakeholder types, roles, and organizations based on the selected values. It also manages form
 * submission to add or update user records. The component also fetches specific user data if an ID is provided.
 *
 * Props:
 * - id: number - The ID of the user to edit; 0 for adding a new user.
 * - activeStake: string - The type of stakeholder (e.g., 'CUSTOMER', 'SERVICEPARTNER').
 * - onCloseDrawer: function - Callback function to close the form drawer.
 * - onRefreshList: function - Callback function to refresh the user list after adding or editing a user.
 */
export default function UserForm(props: TDrawerProps) {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { post } = useFetch();
  const { onShowToast } = useToast();
  const [roleOptions, setRoleOptions] = useState<TRole[]>([]);
  const [selectedRole, setSelectedRole] = useState<TRole>();
  const [role, setRole] = useState<TRole[]>([]);
  const [stateOptions, setStateOptions] = useState<TDropdownOptions[]>([]);
  const [customerOptions, setCustomerOptions] = useState<TDropdownOptions[]>(
    [],
  );
  const [clients, setClients] = useState<Client[]>([]);
  const allSkills = [
    // Frontend
    "HTML",
    "CSS",
    "JavaScript",
    "TypeScript",
    "React",
    "React Native",
    "Next.js",
    "Redux",
    "Zustand",
    "Tailwind CSS",
    "Material UI",
    "Bootstrap",
    "Angular",
    "Vue.js",
    "Svelte",

    // Backend
    "Node.js",
    "Express.js",
    "NestJS",
    "Fastify",
    "GraphQL",
    "REST API",

    // Databases
    "MongoDB",
    "PostgreSQL",
    "MySQL",
    "SQLite",
    "Redis",
    "MariaDB",
    "Firebase",
    "Supabase",

    // DevOps
    "Docker",
    "Kubernetes",
    "CI/CD",
    "Jenkins",
    "GitHub Actions",
    "GitLab CI",
    "AWS",
    "Azure",
    "Google Cloud",
    "Terraform",
    "Ansible",
    "Nginx",

    // Mobile
    "Flutter",
    "Dart",
    "Kotlin",
    "Swift",
    "Ionic",

    // Testing
    "Jest",
    "Mocha",
    "Chai",
    "React Testing Library",
    "Cypress",
    "Playwright",

    // Tools
    "Git",
    "GitHub",
    "Bitbucket",
    "Webpack",
    "Vite",
    "ESLint",
    "Prettier",
    "Babel",

    // Data & AI
    "Python",
    "Pandas",
    "NumPy",
    "Machine Learning",
    "Deep Learning",
    "TensorFlow",
    "PyTorch",
    "Scikit-Learn",
    "Data Analysis",

    // Cloud & Infra
    "Serverless",
    "CloudFormation",
    "API Gateway",
    "Lambda",
    "EC2",
    "S3",
    "Route53",

    // Other
    "Agile",
    "Scrum",
    "Figma",
    "UI/UX",
    "Project Management",
    "Microservices",
    "System Design",
    "WebSockets",
    "MQTT",
    "Message Queues",
    "RabbitMQ",
    "Kafka",
  ];

  const [searchValue, setSearchValue] = useState("");
  const [designationList, setDesignationList] = useState<TDropdownOptions[]>(
    [],
  );
  const [reviewerOptions, setReviewerOptions] = useState<TDropdownOptions[]>([]);
  const filteredSkills = allSkills.filter((skill) =>
    skill.toLowerCase().includes(searchValue.toLowerCase()),
  );

  // const [locationTypes, setLocationTypes] = useState<TDropdownOptions[]>([]);

  // Hook to handle on load operations
  useEffect(() => {
    fetchRoleOptions();
    fetchSpecificUser();
    fetchClients();
    getDesignationList();
    fetchReviewers();
  }, []);

  const fetchReviewers = async () => {
    try {
      const payload = { data: { currentPage: 1, pageSize: -1 } };
      const response = await post(GetReviewerList, payload);
      console.log("Reviewer response:", response);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        setReviewerOptions(
          response.data.map((r: any) => ({
            value: r.id,
            label: r.fullName || r.name || r.emailId,
          }))
        );
      }
    } catch (error) {
      console.log("Error fetching reviewers:", error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await post(
        GetAllClients,
        {},
        undefined,
        process.env.REACT_APP_PUBLIC_MOM_API_BASEURL,
      );

      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        setClients(
          response.data.map((item: { id: string; name: string }) => ({
            clientId: item.id,
            clientName: item.name,
          })),
        );
      } else {
        console.error(
          "Error fetching clients:",
          response.dataResponse.description,
        );
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  // To fetch the designation list
  const getDesignationList = async () => {
    try {
      const payload = {
        data: {
          currentPage: 1,
          pageSize: -1,
        },
      };
      setIsLoading(true);
      const response = await post(GetDesignations, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        const { data } = response;
        setDesignationList(utils.mapDropdownOptions(data));
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Fetches the list of roles for the given stake holder ID.
   * If the response is successful, it sets the role options state with the mapped dropdown options.
   * If the response is not successful, it shows an error toast.
   * Finally, it sets isLoading to false.
   * @param stakeHolderId The stake holder ID
   */
  const fetchRoleOptions = async () => {
    try {
      const requestPayload = {
        data: {
          currentPage: 1,
          pageSize: PageSize,
        },
      };

      const response: TResponseModel = await post(GetRoleList, requestPayload);
      const { dataResponse, data } = response;
      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        setRole(data);
        setRoleOptions(Utils.mapDropdownOptions(data));
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
      setIsLoading(false);
    }
  };

  // Handle on submit event
  const onFinish = async (values: any) => {
    try {
      const { roleId, password, joiningDate, ...restValues } = values;
      const formattedJoinDate = joiningDate ? dayjs(joiningDate).format("YYYY-MM-DD") : null;
      const payload = {
        data: {
          ...restValues,
          id: props.id ? props.id : 0,
          roleId: roleId,
          joiningDate: formattedJoinDate,
          password: props?.id
            ? EncryptUtils.encrypt(password || "")
            : EncryptUtils.encrypt(Utils.generatePassword()),
        },
      };
      setIsLoading(true);

      const response = await post(AddEditUser, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
        form.resetFields();
        props.onRefreshList();
        props.onCloseDrawer();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // To fetch specific config param
  const fetchSpecificUser = async () => {
    if (props.id > 0) {
      try {
        const payload = {
          data: {
            id: props.id,
          },
        };
        setIsLoading(true);
        const response = await post(GetSpecificUserData, payload);
        const { data } = response;
        if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
          const { password, ...restValues } = response.data as any;
          form.setFieldsValue(restValues);
          form.setFieldValue("password", EncryptUtils.decrypt(password || ""));
          setSelectedRole(data?.roles);
          form.setFieldsValue({
            ...data,
            joiningDate: data.joiningDate
              ? dayjs(data.joiningDate)
              : null,
            skills:
              typeof data.skills === "string"
                ? JSON.parse(data.skills)
                : data.skills,
          });
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: response.dataResponse.description,
          });
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <FormLayout>
      <Form form={form} name="register" onFinish={onFinish} layout="vertical">
        <FormHeader>
          <CloseOutlined
            style={{ cursor: "pointer", marginTop: "0.3rem", color: "white" }}
            onClick={() => {
              props.onCloseDrawer();
            }}
          />
        </FormHeader>

        <FormContent>
          {/* <Row gutter={16} className="">
            <Space>
              <Col span={24}>
                <h2 style={{ color: "#fff", marginTop: "10px" }}>
                  {`${props.id ? "Edit" : "Add"} a User`}
                </h2>
              </Col>
            </Space>
          </Row> */}
          <Row gutter={16} className="formRowHeight">
            <Col span={12}>
              <Form.Item
                name="fullName"
                label="Name"
                rules={[
                  {
                    required: true,
                    validator: requiredFieldValidator("enter display name"),
                  },
                ]}
              >
                <Input placeholder="Enter the display name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="emailId"
                label="E-mail"
                rules={[
                  {
                    type: "email",
                    message: "The input is not valid E-mail!",
                  },
                  {
                    required: true,
                    message: "Please enter your E-mail!",
                  },
                ]}
              >
                <Input placeholder="Enter the email address" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16} className="formRowHeight">
            <Col span={12}>
              <Form.Item
                name="mobileNumber"
                label="Phone Number"
                rules={[
                  {
                    required: true,
                    message: "Please enter your phone number!",
                  },
                  {
                    pattern: /^\d{10}$/,
                    message: "Phone number must be exactly 10 digits!",
                  },
                ]}
              >
                <Input maxLength={10} placeholder="Enter the phone number" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="roleId"
                label="Role"
                rules={[{ required: true, message: "Please select role" }]}
              >
                <Select
                  disabled={false}
                  placeholder="Select role"
                  options={roleOptions}
                  onChange={(value, option) => {
                    form.setFieldValue("roleId", value);
                    setSelectedRole((option as any)?.data as TRole);
                  }}
                ></Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16} className="formRowHeight">
            <Col span={12}>
              <Form.Item
                name="clientId"
                label={
                  <span>
                    Client&nbsp;
                    <Tooltip title="User from client and will be able to see all the projects of that client">
                      <InfoCircleOutlined />
                    </Tooltip>
                  </span>
                }
              // rules={[{ required: true, message: "Please select a client" }]}
              >
                <Select
                  placeholder="Select client"
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label as string)
                      ?.toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  {clients.map((client) => (
                    <Select.Option
                      key={client.clientId}
                      value={client.clientId}
                      label={client.clientName}
                    >
                      {client.clientName}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="isActive"
                label="Status"
                rules={[{ required: true, message: "Please select status" }]}
              >
                <Select
                  disabled={false}
                  placeholder="Select status"
                  options={[
                    { value: 1, label: "Active" },
                    { value: 0, label: "Inactive" },
                  ]}
                ></Select>
              </Form.Item>
            </Col>
          </Row>

          {/* {selectedRole?.roleUniqueId === "EMPLOYEE" ||
          selectedRole?.roleUniqueId === "MANAGER" ? ( */}
          <Row gutter={16} className="formRowHeight">
            <Col span={12}>
              <Form.Item
                name="designation"
                label="Designation"
                rules={[{ required: true, message: "Select designation" }]}
              >
                <Select
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      ?.toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  options={designationList}
                  placeholder="Select designation"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="experience"
                label={<span className={styles.customLabel}>Experience</span>}
              // rules={[
              //   {
              //     required: true,
              //     validator: requiredFieldValidator("enter experience"),
              //   },
              // ]}
              >
                <Input placeholder="Enter city" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="skills"
                label={<span className={styles.customLabel}>Skills</span>}
              // rules={[
              //   {
              //     required: true,
              //     message: "Please enter skills",
              //   },
              // ]}
              >
                <Select
                  mode="tags"
                  showSearch
                  placeholder="Enter skills"
                  onSearch={setSearchValue}
                  filterOption={(input, option) => {
                    const label = option?.label;

                    if (typeof label === "string") {
                      return label.toLowerCase().includes(input.toLowerCase());
                    }
                    return false;
                  }}
                >
                  {filteredSkills.map((skill) => (
                    <Select.Option key={skill} value={skill} label={skill}>
                      {skill}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            {props?.id ? (
              <Col span={12}>
                <Form.Item
                  name="password"
                  label="Password"
                  rules={[
                    {
                      required: true,
                      message: "Please enter your password!",
                    },
                    {
                      pattern:
                        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
                      message:
                        "Password must be at least 8 characters long and include uppercase, lowercase, number, and special character!",
                    },
                  ]}
                >
                  <Input.Password placeholder="Enter the password" />
                </Form.Item>
              </Col>
            ) : (
              ""
            )}
            <Col span={12}>
              <Form.Item name="managerId" label="Reviewer">
                <Select
                  showSearch
                  allowClear
                  placeholder="Select Reviewer"
                  options={reviewerOptions}
                  filterOption={(input, option) =>
                    (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="joiningDate" label="Join Date">
                <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
          </Row>

          {/* ) : null} */}
        </FormContent>
        <FormFooter className="formFooter">
          <Button
            type="default"
            className="btnDimensions"
            disabled={isLoading}
            onClick={props.onCloseDrawer}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={form.submit}
            disabled={isLoading}
            className="btnDimensions"
          >
            {props.id ? "Update" : "Add"}
          </Button>
        </FormFooter>
      </Form>
    </FormLayout>
  );
}
